import React from 'react';
import { mergeRefs } from 'react-merge-refs';
import { CSSTransition } from 'react-transition-group';

import { TooltipState } from './Tooltip.hooks';
import { TOOLTIP_SHADOW_TEST_ID } from './Tooltip.test-ids';

import styles from './Tooltip.module.scss';

export interface TooltipAnchorProps {
  /**
   * Tooltip state. Refer to `useTooltip()`
   */
  state: TooltipState;
  /**
   * Render a different element. Useful for non button anchors.
   */
  renderAs?: (
    ref: (instance: HTMLElement | null) => void,
    refProps: Record<string, unknown>,
    children?: React.ReactNode
  ) => React.ReactElement;
}

export const TooltipAnchor = React.forwardRef<HTMLElement, React.HTMLProps<HTMLElement> & TooltipAnchorProps>(
  function TooltipAnchor({ children, state, renderAs, ...props }, forwardedRef) {
    const ref = React.useMemo(() => mergeRefs([state.reference, forwardedRef]), [state.reference, forwardedRef]);
    const refProps = state.getReferenceProps(props);

    if (renderAs) return renderAs(ref, refProps, children);

    return (
      <button ref={ref} {...refProps}>
        {children}
      </button>
    );
  }
);

export const TooltipFloat = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & { state: TooltipState; overlay?: boolean }
>(function TooltipFloat({ state, overlay = true, ...props }, propRef) {
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const ref = React.useMemo(() => mergeRefs([state.floating, propRef]), [state.floating, propRef]);

  if (!overlay && !state.open) return null;

  const arrowX = state.middlewareData.arrow?.x;
  const arrowY = state.middlewareData.arrow?.y;

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[state.placement.split('-')[0]];

  const arrowStyle = {
    left: arrowX != null ? `${arrowX}px` : '',
    top: arrowY != null ? `${arrowY}px` : '',
    right: '',
    bottom: '',
    [staticSide || '']: '-4px',
  };

  const floatingEl = (
    <div
      ref={ref}
      style={{
        position: state.strategy,
        top: state.y ?? 0,
        left: state.x ?? 0,
        visibility: state.x == null ? 'hidden' : 'visible',
        ...props.style,
        maxWidth: '330px',
        boxShadow: '0px 3px 10px 0px rgba(0, 0, 0, 0.16)',
      }}
      {...state.getFloatingProps(props)}
      className={props.className || styles.tooltip}
    >
      {props.children}
      <div ref={state.arrowRef} style={arrowStyle} className={styles.tooltipArrow} />
    </div>
  );

  if (overlay) {
    return (
      <CSSTransition nodeRef={overlayRef} timeout={300} classNames={{ ...styles }} in={state.open} unmountOnExit>
        <div ref={overlayRef} className={styles.overlay} data-testid={TOOLTIP_SHADOW_TEST_ID}>
          {floatingEl}
        </div>
      </CSSTransition>
    );
  }

  return floatingEl;
});
